import React, { Component } from "react";
import PropTypes from "prop-types";
import { Link } from "@quintype/components";

import Carousel from "../../atoms/carousel";
import ResponsiveImageWithFallback from "../../atoms/responsive-image-with-fallback";
import Icon from "../../atoms/icon";
import { PremiumBadgeHeadline } from "../../atoms/premium-badge";
import { isPremium } from "../../../data/story";

import styles from "./styles.m.css";
import { getStoryData, getStoryHeadline, checkAppSlug } from "../../utils/utils";

const StoryCard = ({ story, className = "", type = "small", hasTruncatedHeadline = true }) => {
  if (!story) {
    return null;
  }

  const storyData = getStoryData(story);

  if (!(storyData.headline && story.url)) {
    return null;
  }

  const checkSlug = checkAppSlug();
  let SourceLink = "";

  if (checkSlug) {
    SourceLink = story.url;
  }

  return (
    <Link
      aria-label={`${"Read full story: "} ${storyData.headline}`}
      href={story.url}
      externalLink={SourceLink}
      className={`${className} ${styles["story-card"]}`}
    >
      <div className={styles["image-container"]}>
        <ResponsiveImageWithFallback
          className={styles["image-container-figure"]}
          slug={storyData.imageS3Key}
          key={storyData.imageS3Key}
          metadata={storyData.imageMetadata}
          alt={storyData.imageCaption}
          imgParams={{ auto: ["format", "compress"] }}
          eager
          sources={[
            {
              aspectRatio: [1, 1],
              defaultWidth: 350,
              widths: [350],
              sizes: "30vw"
            }
          ]}
          story={story}
        />
      </div>
      <div className={styles["content"]}>
        <h3 className={`${styles["headline"]} ${hasTruncatedHeadline ? styles["truncated"] : ""} `}>
          <PremiumBadgeHeadline
            className={`${styles.premium}`}
            headline={storyData.headline}
            isPremium={isPremium(story)}
          />
        </h3>
        {type === "big" && (
          <h4 className={`${styles["subheadline"]} ${hasTruncatedHeadline ? styles["truncated"] : ""} `}>
            {story["subheadline"]}
          </h4>
        )}
      </div>
    </Link>
  );
};

StoryCard.propTypes = {
  hasTruncatedHeadline: PropTypes.bool,
  className: PropTypes.string,
  type: PropTypes.string,
  story: PropTypes.shape({
    headline: PropTypes.string.isRequired,
    subheadline: PropTypes.string,
    slug: PropTypes.string.isRequired,
    "hero-image-metadata": PropTypes.shape({
      height: PropTypes.number,
      width: PropTypes.number,
      "aspect-ratio": PropTypes.arrayOf(PropTypes.number)
    }),
    "hero-image-s3-key": PropTypes.string,
    "hero-image-caption": PropTypes.string,
    authors: PropTypes.arrayOf(
      PropTypes.shape({
        "contributor-role": PropTypes.shape({
          name: PropTypes.string
        }),
        name: PropTypes.string
      })
    )
  })
};

class StorySliderAppExclusive extends Component {
  constructor() {
    super();
    this.state = {
      currentSlideIndex: 0
    };

    this.handleSlideChange = this.handleSlideChange.bind(this);
  }

  handleSlideChange(index) {
    this.setState({ currentSlideIndex: index });
  }

  render() {
    const { collection } = this.props;
    if (!collection) {
      return null;
    }

    const items = ([...collection.items] || [])
      .filter(({ story = {} }) => getStoryHeadline(story) && story.url)
      .slice(0, this.props.storyCount || 16);
    let slides = items.map(({ story }) => (
      <StoryCard className={styles["story-card-small"]} key={story.id} story={story} type="small" />
    ));

    return (
      <div
        className={`${styles["base"]}`}
        style={this.props.accentColor && { "--accent-color": this.props.accentColor }}
      >
        <div className={`container`}>
          <h1 className={styles["collection-title"]}>{collection && collection.name}</h1>
          <StoryCard
            story={items[this.state.currentSlideIndex].story}
            className={styles["story-card-big"] + (slides.length === 1 ? ` ${styles["show-on-mobile"]}` : "")}
            type="big"
          />

          {slides.length > 1 && (
            <div className={styles["carousel-wrapper"]}>
              <Carousel
                className={styles["carousel"]}
                afterSlide={this.handleSlideChange}
                options={{
                  type: "carousel",
                  autoplay: 3000,
                  perView: 3,
                  classes: {
                    activeSlide: styles["is-card-active"]
                  },
                  breakpoints: {
                    767: {
                      perView: 2
                    }
                  }
                }}
                renderCenterLeftControls={({ previousSlide }) => (
                  <button
                    aria-label="previous slide"
                    role="button"
                    onClick={previousSlide}
                    className={`${styles["slider-nav-button"]}`}
                  >
                    <Icon type={"slider-prev"} className={styles["slider-button"]} />
                  </button>
                )}
                renderCenterRightControls={({ nextSlide }) => (
                  <button
                    aria-label="next slide"
                    role="button"
                    onClick={nextSlide}
                    className={`${styles["slider-nav-button"]}`}
                  >
                    <Icon type={"slider-next"} className={styles["slider-button"]} />
                  </button>
                )}
                renderBottomCenterControls={({ currentSlide, goToSlide }) => {
                  return (
                    <div className={styles.bullets}>
                      {items.map(({ story }, index) => (
                        <button
                          aria-label="Slider Navigation"
                          role="button"
                          key={story.id}
                          className={
                            currentSlide === index
                              ? `${styles["bullet"]} ${styles["is-active"]}`
                              : `${styles["bullet"]}`
                          }
                          onClick={() => {
                            goToSlide(index);
                          }}
                        />
                      ))}
                    </div>
                  );
                }}
              >
                {slides}
              </Carousel>
            </div>
          )}
        </div>
      </div>
    );
  }
}

StorySliderAppExclusive.propTypes = {
  storyCount: PropTypes.number,
  accentColor: PropTypes.string,
  collection: PropTypes.shape({
    items: PropTypes.arrayOf(PropTypes.shape(StoryCard.propTypes))
  })
};

export default StorySliderAppExclusive;
